import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

const DEFAULT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_THRESHOLD_MS = 30 * 1000; // show the "are you still there?" countdown for the last 30s
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

/** Get inactivity timeout in ms, respecting localStorage override from Developer Options */
function getInactivityTimeoutMs(): number {
  const stored = localStorage.getItem('inactivity_timeout_ms');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  return DEFAULT_INACTIVITY_TIMEOUT_MS;
}

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private timer: any = null;
  private countdownInterval: any = null;
  private boundReset: () => void;
  private showingWarning = false;

  private remainingTimeSubject = new BehaviorSubject<number>(0);
  public remainingTime$ = this.remainingTimeSubject.asObservable();

  private showWarningSubject = new BehaviorSubject<boolean>(false);
  public showWarning$ = this.showWarningSubject.asObservable();

  // Avoid a hard import of SharedService to prevent circular deps — caller provides a clear callback.
  private clearUserFn: (() => void) | null = null;

  /** Register a callback that clears the logged-in user (called by SharedService after construction). */
  registerClearUser(fn: () => void) {
    this.clearUserFn = fn;
  }

  constructor(private router: Router, private ngZone: NgZone) {
    this.boundReset = this.handleActivity.bind(this);
  }

  /** Call this after a successful login to start watching for inactivity. */
  startWatching() {
    // Respect the developer toggle - default is enabled
    localStorage.setItem('inactivity_timer_enabled', 'true'); // remove once in production
    const setting = localStorage.getItem('inactivity_timer_enabled');
    if (setting === 'false') return;

    this.stopWatching(); // clear any existing timer first
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, this.boundReset, { passive: true })
    );
    this.resetTimer();
  }

  /** Call this on logout to clean up listeners. */
  stopWatching() {
    ACTIVITY_EVENTS.forEach(event =>
      window.removeEventListener(event, this.boundReset)
    );
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.showingWarning = false;
    this.showWarningSubject.next(false);
    this.remainingTimeSubject.next(0);
  }

  /** Passive activity (mouse/keyboard) resets the timer, but once the "still there?"
   * warning is showing, only an explicit response from the modal should dismiss it. */
  private handleActivity() {
    if (this.showingWarning) return;
    this.resetTimer();
  }

  /** Called by the countdown modal when the user confirms they're still there. */
  extendSession() {
    this.showingWarning = false;
    this.showWarningSubject.next(false);
    this.resetTimer();
  }

  /** Called by the countdown modal if the user chooses to log out immediately. */
  logoutNow() {
    this.signOut();
  }

  private resetTimer() {
    if (this.timer) clearTimeout(this.timer);
    if (this.countdownInterval) clearInterval(this.countdownInterval);

    const timeoutMs = getInactivityTimeoutMs();
    this.remainingTimeSubject.next(timeoutMs);

    // Run outside Angular's change detection to avoid unnecessary cycles
    this.ngZone.runOutsideAngular(() => {
      let timeLeft = timeoutMs;

      this.countdownInterval = setInterval(() => {
        timeLeft -= 1000;
        this.remainingTimeSubject.next(timeLeft);

        if (timeLeft <= WARNING_THRESHOLD_MS && !this.showingWarning) {
          this.showingWarning = true;
          this.ngZone.run(() => this.showWarningSubject.next(true));
        }
      }, 1000);

      this.timer = setTimeout(() => {
        this.ngZone.run(() => this.signOut());
      }, timeoutMs);
    });
  }

  private signOut() {
    this.stopWatching();
    localStorage.removeItem('login_remember_me');
    localStorage.removeItem('remember_me_token');
    if (this.clearUserFn) this.clearUserFn();
    this.router.navigate(['/login']);
  }

  ngOnDestroy() {
    this.stopWatching();
  }
}
